import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AlertBanner,
  Box,
  Loader,
  MenuInput,
  Typography,
} from '@saviynt/design-system';

import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
  LOAD_INCREMENT_LIST,
} from '../../../../constants/obNextGenConstants';
import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type RootState } from '../../../../redux/obNextGenStore';
import { type ConnectionPayload } from '../../data-layer/ApplicationCard';
import {
  useExistingConnectionApiMutation,
  useGetConnectionListQuery,
} from '../../data-layer/ApplicationSlice';
import { type DiscoveryExistingConnectionState } from '../../data-layer/DiscoveryExistingConnectionSlice';
import {
  type DiscoverySaveConnectionState,
  resetDiscoverySaveConnectionState,
} from '../../data-layer/DiscoverySaveConnectionSlice';
import { resetExistingConnectionDetailsState } from '../../data-layer/ExistingConnectionSlice';

import './ExistingConnection.css';

interface ExistingConnectionProps {
  image: string;
  title: string | undefined;
  total: number;
  appTypeKey: number;
  applicationType: string;
  connectionType: string;
  firstOptionKey: number;
  existingConnectionPayload: (value: ConnectionPayload) => void;
}

interface DropDownOption {
  label: string;
  value: string;
  isChecked?: boolean;
}

/**
 * Existing Connection
 */
function ExistingConnection({
  image,
  title,
  total,
  appTypeKey,
  applicationType,
  connectionType,
  firstOptionKey,
  existingConnectionPayload,
}: ExistingConnectionProps) {
  const dispatch = useDispatch();
  const { GET_APPLICATION_ICONS } = useMemo(getObNextGenApiUrlLists, []);
  const [externalConnectionKey, setExternalConnectionKey] = useState<
    number | string
  >(firstOptionKey);
  const [options, setOptions] = useState<DropDownOption[]>([]);
  const formatMessage = useLocalization();
  const [showBanner, setShowBanner] = useState(false);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const existingConnectionErrorData = useSelector(
    (state: RootState): DiscoveryExistingConnectionState =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
      state.discoveryExistingConnection.errorData
  );
  const saveConnectionData = useSelector(
    (state: RootState): DiscoverySaveConnectionState =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      state.discoverySaveConnection
  );

  const { data: connectionListPaginationResponse, isFetching } =
    useGetConnectionListQuery({
      appTypeKey,
      page,
      size: LOAD_INCREMENT_LIST,
      search,
    });

  const generatePayloadApi = useMemo(
    () => ({
      externalConnectionKey: externalConnectionKey ?? firstOptionKey,
      applicationType,
      connectionType,
      appTypeKey,
      parentCategoryName: 'configuration',
      categoryName: 'Connection',
    }),
    [
      appTypeKey,
      applicationType,
      connectionType,
      externalConnectionKey,
      firstOptionKey,
    ]
  );

  const [
    existingConnectionApi,
    { data: existingConnetionData, isLoading: isExistingConnetionDataLoading },
  ] = useExistingConnectionApiMutation();

  const connectionDetailsAttributes =
    existingConnetionData?.configuration.categories[0].subcategories[0]
      .groups[0].attributes;

  useEffect(() => {
    if (connectionDetailsAttributes && connectionDetailsAttributes.length > 0) {
      const existingConnectionApiPayload: ConnectionPayload = {
        discoveryApplicationName: '',
        appTypeKey: appTypeKey.toString(),
        connectiontype: connectionType,
        categoryName: 'Connection',
        externalConnectionKey: externalConnectionKey.toString(),
      };

      options.forEach((option) => {
        if (option.isChecked) {
          existingConnectionApiPayload.discoveryApplicationName = option.label;
        }
      });

      connectionDetailsAttributes.forEach((field) => {
        if (field.answerAttribute !== 'discoveryApplicationName') {
          existingConnectionApiPayload[field.answerAttribute] =
            field.attributeValue;
        }
      });

      existingConnectionPayload(existingConnectionApiPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appTypeKey,
    connectionDetailsAttributes,
    connectionType,
    externalConnectionKey,
    options,
  ]);

  useEffect(() => {
    if (externalConnectionKey !== null && externalConnectionKey !== '') {
      // eslint-disable-next-line no-void
      void existingConnectionApi(generatePayloadApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingConnectionApi, generatePayloadApi, firstOptionKey]);

  useEffect(() => {
    setOptions([]);
    setPage(0);
    setHasMoreOptions(true);
  }, [search]);

  useEffect(() => {
    if (connectionListPaginationResponse?.list) {
      const newOptions: DropDownOption[] =
        connectionListPaginationResponse.list.map((entry) => ({
          key: entry.connectionName.concat(
            entry.externalConnectionKey.toString()
          ),
          label: entry.connectionName,
          value: entry.externalConnectionKey.toString(),
          isChecked: entry.externalConnectionKey === externalConnectionKey,
        }));

      if (page === 0) {
        setOptions(newOptions);
      } else {
        setOptions((prev) => [...prev, ...newOptions]);
      }

      setHasMoreOptions(connectionListPaginationResponse.nextPage !== null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionListPaginationResponse, search]);

  //
  const handleScroll = useCallback(() => {
    if (!hasMoreOptions || isLoading || isFetching) {
      return;
    }

    setIsLoading(true);
    setPage((prevPage) => prevPage + 1);
  }, [hasMoreOptions, isLoading, isFetching]);

  const handleOptionChange = (selectedOption: {
    key: string;
    value: string;
    label: string;
    isChecked: boolean;
  }) => {
    setExternalConnectionKey(parseInt(selectedOption.value, 10));
    const updateOptions = options.map((option) => {
      if (option.value === selectedOption.value) {
        return { ...option, isChecked: !option.isChecked };
      }

      return {
        ...option,
        isChecked: false,
      };
    });

    setOptions(updateOptions);
  };

  const handleInputReset = useCallback(() => {
    setSearch('');
    setPage(0);
    setHasMoreOptions(true);
  }, []);

  const handleSearch = (value: string) => {
    setSearch(value);
    setExternalConnectionKey('');
  };

  const handleBannerClose = () => {
    setShowBanner(false);
    dispatch(resetExistingConnectionDetailsState());
    dispatch(resetDiscoverySaveConnectionState());
  };

  useMemo(() => {
    if (
      existingConnectionErrorData !== null ||
      saveConnectionData.errorData !== null
    ) {
      setShowBanner(true);
    }
  }, [existingConnectionErrorData, saveConnectionData]);

  const handleConncetionMenu = () =>
    connectionListPaginationResponse && (
      <MenuInput
        dataTestId={testIds.DISCOVERY_EXISTING_CONNECTION_DROPDOWN}
        searchTerm={search}
        onSearchTermChange={handleSearch}
        placeholder='Please Select Your Connection from the list'
        className='ExistingConnection-Content-List'
        hasMoreOptions={hasMoreOptions}
        loadingText='Loading more results'
        onScroll={handleScroll}
        options={options}
        onChange={handleOptionChange}
        isLoading={isLoading}
        resetSignal={false}
        onInputReset={handleInputReset}
      />
    );

  return (
    <div className='ExistingConnection'>
      <div className='ExistingConnection-Header'>
        <Box>
          {(existingConnectionErrorData !== null ||
            saveConnectionData.errorData !== null) && (
            <AlertBanner
              className='ExistingConnection-Header-Banner'
              isVisible={showBanner}
              colorTheme='Critical'
              title='Test Failed'
              description={
                existingConnectionErrorData !== null
                  ? JSON.stringify(existingConnectionErrorData)
                  : JSON.stringify(saveConnectionData.errorData)
              }
              onCancel={() => {
                handleBannerClose();
              }}
            />
          )}
        </Box>
        <Box className='ExistingConnection-Header-Details'>
          <img
            className='ExistingConnection-Header-Details-Icon'
            src={`${getBaseUrlObNextGen()}${GET_APPLICATION_ICONS}/${image}`}
            alt='Application icon'
          />
          <span className='ExistingConnection-Header-Details-Title'>
            <Typography kind='h3' color='--color-foreground-neutral-bold'>
              {formatMessage(
                title ??
                  'INTEGRATIONS.Feature.Basic.ServiceNow.discovery.Conn.Title',
                'Application'
              )}
            </Typography>
          </span>
        </Box>
        <Box className='ExistingConnection-Header-SubHeading'>
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {`${formatMessage(
              'INTEGRATION.EXISTING.CONNECTION.SUBTEXT',
              'We discovered Some Existing Connection',
              {
                total,
              }
            )}
            `}
          </Typography>
        </Box>
      </div>
      <section className='ExistingConnection-Content'>
        <Box>
          <Box>
            <Typography kind='h4' color='--color-foreground-neutral-bold'>
              {total > 1
                ? formatMessage(
                    'INTEGRATION.DISCOVERY.SELECT_EXISTING',
                    'Select Existing Connection'
                  )
                : formatMessage(
                    'INTEGRATION.DISCOVERY.CONNECTION.NAME',
                    'Connection Name'
                  )}
            </Typography>
          </Box>
          <Box>
            {total > 1
              ? handleConncetionMenu()
              : connectionListPaginationResponse?.list[0].connectionName}
          </Box>
        </Box>
        <Box className='ExistingConnection-Content-Section'>
          {isExistingConnetionDataLoading ? (
            <Loader
              className='ExistingConnection-Content-Section-Loader'
              color='brand'
              kind='loop'
              format='main'
            />
          ) : (
            <Box>
              {connectionDetailsAttributes?.map(
                (field: {
                  answerAttribute: string;
                  attributeName: string;
                  attributeDisplayName: string;
                  attributeValue:
                    | string
                    | number
                    | boolean
                    | React.ReactElement
                    | Iterable<React.ReactNode>
                    | React.ReactPortal
                    | null
                    | undefined;
                }) => {
                  if (
                    field.attributeName === 'CONNECTIONNAME' ||
                    field.attributeName === 'ConnectionJSON'
                  ) {
                    return null;
                  }

                  if (field.attributeValue !== null) {
                    return (
                      <Box>
                        <Typography
                          kind='label'
                          className='ExistingConnection-Content-List-Label'
                          color='--color-foreground-secondary-bold'>
                          {field.attributeDisplayName &&
                            formatMessage(
                              field.attributeDisplayName,
                              'Default Label'
                            )}
                        </Typography>
                        <Typography
                          kind='body2'
                          color='--color-foreground-secondary-bold'>
                          {field.answerAttribute === 'password'
                            ? '•••••••••••••••••••••••'
                            : field.attributeValue}
                        </Typography>
                      </Box>
                    );
                  }

                  return null;
                }
              )}
            </Box>
          )}
        </Box>
      </section>
    </div>
  );
}

export default ExistingConnection;
